/* This file only ever runs in a browser, and the project's tsconfig is React
   Native's - no DOM. Pulling the DOM types in here rather than project-wide
   keeps `window` and `document` from appearing to exist in the phone code,
   where reaching for either is a crash. */
/// <reference lib="dom" />

import { colors } from '../theme';
import type { CheckoutHandoff, CheckoutResult } from './payments';
import { toPaise } from './payments';

/**
 * Razorpay's checkout, opened in a browser - the web twin of
 * `services/razorpay.ts`.
 *
 * ---------------------------------------------------------------------------
 * WHY A TWIN AND NOT A SHIM
 * ---------------------------------------------------------------------------
 * `react-native-razorpay` is the native SDK: it reaches for
 * `TurboModuleRegistry`, which react-native-web has nothing to give. Importing
 * it does not merely fail at checkout time, it fails at BUILD time and takes
 * the whole web bundle down with it. So the web build resolves `./razorpay` to
 * this file instead - that is what the `extensions` order in `vite.config.mjs`
 * does - and the native package is never reached from a browser.
 *
 * The exports are deliberately identical - `openRazorpay`, `CheckoutFailure`,
 * `CheckoutFailureKind` - because `services/payFlow.ts` is shared code and
 * must not learn which platform it is on.
 *
 * ---------------------------------------------------------------------------
 * SAME ORDER, SAME SIGNATURE, SAME SERVER
 * ---------------------------------------------------------------------------
 * This is not a second payment path. The server mints the order the same way,
 * checkout.js is handed the same `order_id`, and the same three fields come
 * back for `POST /payment/verify` to check. Everything the native file says
 * about `order_id` being non-optional, and about a rejection not being proof
 * that nothing was charged, is true here word for word.
 */

/** Why the sheet closed without a payment we can verify. */
export type CheckoutFailureKind =
  /** The customer backed out. Nothing was charged. */
  | 'cancelled'
  /** Could not reach the gateway. May still have gone through - ask the server. */
  | 'network'
  /** The gateway refused the payment: bad card, insufficient funds, timeout. */
  | 'declined'
  /** Our own options were wrong, or the SDK is not linked. A bug, not a decline. */
  | 'setup';

export class CheckoutFailure extends Error {
  constructor(
    readonly kind: CheckoutFailureKind,
    message: string,
    readonly code?: number,
  ) {
    super(message);
    this.name = 'CheckoutFailure';
  }
}

/* Razorpay's own script, and the only third party this app loads at runtime.
   It has to come from their domain: a self-hosted copy is unsupported and
   stops working the day they change the checkout. */
const CHECKOUT_SCRIPT = 'https://checkout.razorpay.com/v1/checkout.js';

/** What checkout.js hands back on success - snake_case, like the native SDK. */
type WebPaymentSuccess = {
  razorpay_payment_id?: string;
  razorpay_order_id?: string;
  razorpay_signature?: string;
};

/**
 * The web SDK's failure, which is shaped nothing like the native one: string
 * codes rather than the numbers `com.razorpay.Checkout` emits.
 */
type WebPaymentError = {
  error?: {
    code?: string;
    description?: string;
    reason?: string;
  };
};

type RazorpayInstance = {
  open: () => void;
  close: () => void;
  on: (event: string, handler: (event: WebPaymentError) => void) => void;
};

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => RazorpayInstance;
  }
}

/*
 * Loaded once and remembered, including across a failed attempt the customer
 * retries - a second <script> for the same src re-runs the whole checkout
 * bundle and leaves two copies fighting over one modal.
 */
let loading: Promise<void> | null = null;

function loadCheckout(): Promise<void> {
  if (window.Razorpay) return Promise.resolve();

  if (!loading) {
    loading = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = CHECKOUT_SCRIPT;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        /* Forget the failure, so a retry gets a fresh <script> rather than
           this same rejected promise for the rest of the session. */
        loading = null;
        reject(
          new CheckoutFailure('network', 'Could not reach the payment gateway'),
        );
      };
      document.head.appendChild(script);
    });
  }

  return loading;
}

/**
 * Razorpay's web error codes, which are strings and a shorter list than the
 * native SDK's numbers.
 *
 * `SERVER_ERROR` is deliberately read as `network` rather than `declined`: it
 * means the gateway broke somewhere after the payment was handed over, which
 * is precisely the case where money may have moved without us hearing about
 * it. `payFlow` turns `network` into "ask the server", and that is the answer
 * this case needs.
 */
function classify(code: string | undefined): CheckoutFailureKind {
  switch (code) {
    case 'NETWORK_ERROR':
    case 'SERVER_ERROR':
      return 'network';
    default:
      /*
       * Everything else - `BAD_REQUEST_ERROR`, `GATEWAY_ERROR` - reaches us
       * only after the gateway has looked at a real payment and refused it.
       * Treated as a decline for the same reason the native file treats its
       * unknown codes that way: "your card was refused" is the kinder wrong
       * answer than "something is broken in the app".
       */
      return 'declined';
  }
}

/**
 * Opens the sheet and resolves with what the server needs to verify.
 *
 * Rejects with a {@link CheckoutFailure} - never with Razorpay's raw object,
 * which is not an `Error` and would defeat every `instanceof Error` check
 * between here and the screen.
 */
export async function openRazorpay(
  handoff: CheckoutHandoff,
): Promise<CheckoutResult> {
  await loadCheckout();

  const Razorpay = window.Razorpay;
  if (!Razorpay) {
    throw new CheckoutFailure('setup', 'The payment gateway did not load');
  }

  return new Promise<CheckoutResult>((resolve, reject) => {
    /*
     * checkout.js calls back more than once for a single attempt: a
     * `payment.failed` is followed by the modal closing, which is a dismissal
     * too. The first outcome is the true one; the rest are echoes.
     */
    let settled = false;
    /* What `payment.failed` said, so the dismissal that follows it reports the
       decline instead of pretending the customer walked away. */
    let failure: CheckoutFailure | null = null;

    const fail = (error: CheckoutFailure) => {
      if (settled) return;
      settled = true;
      reject(error);
    };

    const succeed = (result: CheckoutResult) => {
      if (settled) return;
      settled = true;
      resolve(result);
    };

    const checkout = new Razorpay({
      key: handoff.keyId,
      /* Paise. The server minted the order in paise from the same rupee
         string, and a mismatch here is refused by the gateway outright. */
      amount: toPaise(handoff.amount),
      currency: handoff.currency,
      order_id: handoff.razorpayOrderId,
      name: 'Homingo',
      description: handoff.reference,
      prefill: {
        ...(handoff.customerName ? { name: handoff.customerName } : {}),
        ...(handoff.customerContact
          ? { contact: handoff.customerContact }
          : {}),
      },
      theme: { color: colors.primary },
      modal: {
        /* Ask before closing, exactly as the phone does - a stray click on the
           backdrop mid-UPI otherwise abandons a payment already in flight at
           the bank. */
        confirm_close: true,
        ondismiss: () => {
          fail(
            failure ??
              new CheckoutFailure('cancelled', 'Payment window was closed'),
          );
        },
      },
      handler: (payment: WebPaymentSuccess) => {
        /*
         * A success with no signature is not a success we can act on. It
         * happens when the sheet was opened without an `order_id` - the money
         * is taken, but there is nothing for the server to verify it against.
         */
        if (
          !payment.razorpay_order_id ||
          !payment.razorpay_payment_id ||
          !payment.razorpay_signature
        ) {
          fail(
            new CheckoutFailure(
              'setup',
              'Razorpay returned a payment with no order or signature',
            ),
          );
          return;
        }

        succeed({
          razorpayOrderId: payment.razorpay_order_id,
          razorpayPaymentId: payment.razorpay_payment_id,
          signature: payment.razorpay_signature,
        });
      },
    });

    checkout.on('payment.failed', event => {
      failure = new CheckoutFailure(
        classify(event.error?.code),
        event.error?.description ?? 'The payment did not go through',
      );
      /* The web modal stays up on a decline, offering another try. Closing it
         hands the outcome back now - and the caller's next move is to ask the
         server what really happened, which is worth more than a retry against
         a gateway that has just said no. */
      checkout.close();
    });

    checkout.open();
  });
}
